import {useContext, useState} from 'react';
import {Box, Button, Stack, Typography} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {EntityServicesRepository} from '../../../../../../repositories/entity-services';
import {EntityService} from '../../../../../../backend/interfaces/entity-service';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../../i18n/translations';
import {useHistory, useLocation, useParams, useRouteMatch} from 'react-router-dom';
import {AppContextType} from '../../../../../../contexts/app/types';
import {AppContext} from '../../../../../../contexts/app/context';
import {ScreenPaths} from '../../../../../../enums/screen-path';
import {appColors} from '../../../../../../utils/constants/colors';
import {PaginatedTableData} from '../../../../../../interfaces/paginated-table-data';
import {PaginatedTable} from '../../../../../../components/paginated-table/paginated-table';
import {usePaginatedTable} from '../../../../../../hooks';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import SearchInput from '../../../../../../components/search-input/search-input';
import {Modal} from '../../../../../../components/modal/modal';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Order = 'order',
  State = 'state',
}

export default function EntityServicesProfessionalCaregiverOfficeScreen({
  canWrite,
}: {
  canWrite: boolean;
}) {
  let {entityId}: {entityId?: string} = useParams();
  const appContext: AppContextType = useContext(AppContext);
  let history = useHistory();
  const [deletedServiceTitle, setDeletedServiceTitle] = useState<string | null>(
    new URLSearchParams(useLocation().search).get(ScreenPaths.EntityServicesDeletedQuery)
  );
  const {t} = useTranslation();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {path, url} = useRouteMatch();

  // Fetch services
  const handleGetServices = async (limit: number, skip: number, search?: string) => {
    if (!entityId) {
      throw new Error('No entity.');
    }

    // Load services
    const result: {totalCount: number; data: Array<EntityService>} =
      await EntityServicesRepository.instance.getInEntity(entityId, limit, skip, search);

    // Process data
    const processedData: PaginatedTableData<EntityService> = result.data.map((obj) => {
      return {
        id: obj.id || '',
        item: obj,
        enabled: obj.isActive,
        toLink: url.charAt(url.length - 1) === '/' ? `${url}${obj.id}` : `${url}/${obj.id}`,
        avatarUrl: obj.iconUrl,
        avatarBackgroundColor: appColors.appButtons.entityServices.enabled,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            value: obj.title || '',
          },
          {
            columnId: ColumnIdentifier.Order,
            value: obj.displayOrder ? obj.displayOrder.toString() : '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.isActive
              ? t(translations.entity_services.root.state_value_true)
              : t(translations.entity_services.root.state_value_false),
          },
        ],
      };
    });

    // Return
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    // Reset state
    setDeletedServiceTitle(null);

    // Reset history
    history.replace(path.replace(':entityId', entityId || ''));
  };

  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
  } = usePaginatedTable<EntityService>({
    fetchData: handleGetServices,
  });

  return (
    <>
      <GenericHeader
        titleCrumbs={[
          {
            label: t(translations.office.professional_caregiver.tabs.digital_inclusion),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}`,
          },
          {
            label: t(translations.screen_paths.institution_settings_services),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Services}`,
          },
        ]}
        isLoading={false}
        leftColumn={
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              paddingTop: '10px',
            }}
          >
            <SearchInput
              placeholder={t(translations.entity_services.root.search_input_placeholder)}
              value={currentSearch}
              onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
              onSearchAction={(value: string) => handleApplyFilters(1, value)}
            />
          </Stack>
        }
        rightColumn={
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              paddingTop: '10px',
            }}
          >
            <Button
              variant="inverted"
              endIcon={<AddIcon />}
              onClick={() =>
                history.push(url.charAt(url.length - 1) === '/' ? `${url}create` : `${url}/create`)
              }
              disabled={!canWrite}
            >
              {t(translations.entity_services.root.add_button)}
            </Button>
          </Stack>
        }
      />
      <Box className="listing-container">
        <Box className="listing-container-centered">
          <PaginatedTable
            avatar={{
              type: 'entity-service-icon',
              isIcon: true,
            }}
            emptyTableMessage={t(translations.entity_services.root.no_services)}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(translations.entity_services.root.name_header),
                flexGrow: 2,
              },
              {
                columnId: ColumnIdentifier.Order,
                title: t(translations.entity_services.root.display_order_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.entity_services.root.state_header),
                flexGrow: 1,
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={true}
          />
        </Box>
      </Box>
      <Modal
        id="service-deleted-warning"
        open={deletedServiceTitle ? true : false}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        actions={[
          {
            label: t(translations.entity_services.root.deleted_service_dialog_ok_button),
            type: 'nondestructive',
            onClick: handleCloseDialog,
          },
        ]}
      >
        <Stack>
          <Typography
            sx={{
              maxWidth: '90%',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '24px',
              textAlign: 'center',
              margin: '42px auto 0 auto',
            }}
          >
            {t(translations.entity_services.root.deleted_service_dialog_title, {
              name: deletedServiceTitle,
            })}
          </Typography>
          <Typography
            sx={{
              margin: '24px auto',
              maxWidth: '85%',
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '21px',
              textAlign: 'center',
              color: 'var(--dcm-gray-input2)',
            }}
          >
            {t(translations.entity_services.root.deleted_service_dialog_message1, {
              name: deletedServiceTitle,
            })}
            <br />
            <br />
            {t(translations.entity_services.root.deleted_service_dialog_message2, {
              caregiversAppName: appContext.state.currentThemeSettings.caregiversAppName,
              seniorsDeviceName: appContext.state.currentThemeSettings.seniorsDeviceName,
            })}
          </Typography>
        </Stack>
      </Modal>
    </>
  );
}
